"use client";

import { useEffect } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { SiInstagram, SiTiktok, SiYoutube } from "react-icons/si";

type SocialVideoLink = {
  platform: "instagram" | "tiktok" | "youtube" | "threads";
  href?: string | null;
};

type EmbedItem = {
  key: string;
  title: string;
  href: string;
  kind: "iframe" | "instagram";
  iframeSrc?: string;
  Icon: typeof SiYoutube;
};

declare global {
  interface Window {
    instgrm?: {
      Embeds?: {
        process?: () => void;
      };
    };
  }
}

function safeHttpsUrl(raw: string | null | undefined): string | null {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return null;
  try {
    const url = new URL(s.includes("://") ? s : `https://${s}`);
    if (url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

function youtubeEmbedSrc(href: string): string | null {
  try {
    const url = new URL(href);
    const host = url.hostname.toLowerCase();
    let id = "";
    if (host === "youtu.be") {
      id = url.pathname.replace(/^\/+/, "").split("/")[0] || "";
    } else if (host.includes("youtube.com")) {
      if (url.pathname.startsWith("/watch")) {
        id = url.searchParams.get("v") || "";
      } else {
        const parts = url.pathname.split("/").filter(Boolean);
        if (parts[0] === "shorts" || parts[0] === "embed" || parts[0] === "live") id = parts[1] || "";
      }
    }
    id = id.trim();
    if (!/^[A-Za-z0-9_-]{6,}$/.test(id)) return null;
    return `https://www.youtube.com/embed/${encodeURIComponent(id)}?rel=0&modestbranding=1&playsinline=1`;
  } catch {
    return null;
  }
}

function tiktokEmbedSrc(href: string): string | null {
  try {
    const url = new URL(href);
    const host = url.hostname.toLowerCase();
    if (!host.includes("tiktok.com")) return null;
    const match = url.pathname.match(/\/video\/(\d+)/);
    const id = match?.[1] || "";
    if (!id) return null;
    return `https://www.tiktok.com/player/v1/${encodeURIComponent(id)}`;
  } catch {
    return null;
  }
}

function instagramEmbedHref(href: string): string | null {
  try {
    const url = new URL(href);
    const host = url.hostname.toLowerCase();
    if (!host.includes("instagram.com")) return null;
    const parts = url.pathname.split("/").filter(Boolean);
    const kind = parts[0] || "";
    const id = parts[1] || "";
    if (!id) return null;
    if (!["p", "reel", "reels", "tv"].includes(kind)) return null;
    return `https://www.instagram.com/${kind}/${id}/`;
  } catch {
    return null;
  }
}

function normalizeEmbeds(links: SocialVideoLink[]): EmbedItem[] {
  const out: EmbedItem[] = [];
  for (const link of links) {
    const href = safeHttpsUrl(link.href);
    if (!href) continue;

    if (link.platform === "youtube") {
      const iframeSrc = youtubeEmbedSrc(href);
      if (!iframeSrc) continue;
      out.push({
        key: `youtube:${href}`,
        title: "YouTube",
        href,
        kind: "iframe",
        iframeSrc,
        Icon: SiYoutube,
      });
      continue;
    }

    if (link.platform === "tiktok") {
      const iframeSrc = tiktokEmbedSrc(href);
      if (!iframeSrc) continue;
      out.push({
        key: `tiktok:${href}`,
        title: "TikTok",
        href,
        kind: "iframe",
        Icon: SiTiktok,
        iframeSrc,
      });
      continue;
    }

    if (link.platform === "instagram") {
      const embedHref = instagramEmbedHref(href);
      if (!embedHref) continue;
      out.push({
        key: `instagram:${embedHref}`,
        title: "Instagram",
        href: embedHref,
        kind: "instagram",
        Icon: SiInstagram,
      });
    }
  }
  return out;
}

export function SocialVideoEmbeds({
  links,
  className = "",
}: {
  links: SocialVideoLink[];
  className?: string;
}) {
  const embeds = normalizeEmbeds(links);

  useEffect(() => {
    const hasInstagram = embeds.some((item) => item.kind === "instagram");
    if (!hasInstagram || typeof document === "undefined") return;

    const processEmbeds = () => {
      try {
        window.instgrm?.Embeds?.process?.();
      } catch {
        // ignore
      }
    };

    const existing = document.querySelector<HTMLScriptElement>('script[data-teravia-instagram-embed="true"]');
    if (existing) {
      processEmbeds();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.instagram.com/embed.js";
    script.setAttribute("data-teravia-instagram-embed", "true");
    script.onload = () => processEmbeds();
    document.body.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [embeds]);

  if (!embeds.length) return null;

  return (
    <div className={`grid gap-3 ${embeds.length > 1 ? "xl:grid-cols-2" : ""} ${className}`.trim()}>
      {embeds.map(({ key, title, href, kind, iframeSrc, Icon }) => (
        <div key={key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white/70">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white/80 px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <Icon className="shrink-0 text-slate-800" />
              <span className="truncate text-sm font-semibold text-slate-900">{title}</span>
            </div>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              aria-label={`Open ${title}`}
              title={`Open ${title}`}
            >
              <FiArrowUpRight size={14} />
            </a>
          </div>
          {kind === "instagram" ? (
            <div className="bg-white px-2 py-3 sm:px-3">
              <blockquote
                className="instagram-media !m-0 !min-w-0 !max-w-none rounded-xl"
                data-instgrm-captioned=""
                data-instgrm-permalink={href}
                data-instgrm-version="14"
                style={{
                  background: "#fff",
                  border: 0,
                  margin: 0,
                  maxWidth: "100%",
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <a href={href} target="_blank" rel="noopener noreferrer">
                  View this post on Instagram
                </a>
              </blockquote>
            </div>
          ) : (
            <div className="relative aspect-video bg-slate-100">
              <iframe
                src={iframeSrc}
                title={`${title} video`}
                className="absolute inset-0 h-full w-full border-0"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
